
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2, Languages, MessageSquareText, Sparkles } from "lucide-react";
import { Question } from "@/types/quiz";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

interface LanguageExplanationProps {
  question: Question;
  questionNumber: number;
}

// Supported languages for translations
const supportedLanguages = [
  { value: "english", label: "English" },
  { value: "hindi", label: "Hindi" },
  { value: "tamil", label: "Tamil" },
  { value: "marathi", label: "Marathi" },
  { value: "telugu", label: "Telugu" },
  { value: "bengali", label: "Bengali" },
  { value: "gujarati", label: "Gujarati" },
  { value: "kannada", label: "Kannada" },
  { value: "punjabi", label: "Punjabi" },
  { value: "hinglish", label: "Hinglish" } // Hindi-English mix
];

const LanguageExplanation: React.FC<LanguageExplanationProps> = ({ question, questionNumber }) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>("english");
  const [explanation, setExplanation] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAiGenerated, setIsAiGenerated] = useState<boolean>(false);

  // Get the correct answer text
  const correctAnswer = question.options[question.correctanswer];
  
  // Function to translate the explanation
  const translateExplanation = async () => {
    if (selectedLanguage === "english") {
      setExplanation(question.explanation || "No explanation available for this question.");
      return;
    }

    setIsLoading(true);
    try {
      // Check if we have a cached translation
      const cachedExplanation = sessionStorage.getItem(`explanation-${question.id}-${selectedLanguage}`);
      
      if (cachedExplanation) {
        setExplanation(cachedExplanation);
        setIsLoading(false);
        return;
      }

      // Call the AI Tutoring service to translate
      const { data, error } = await supabase.functions.invoke('ai-tutoring', {
        body: {
          action: 'explain',
          data: {
            subject: question.category || question.subject || 'General Knowledge',
            question: question.question,
            options: question.options,
            correctAnswer: correctAnswer,
            language: selectedLanguage
          }
        }
      });

      if (error) {
        throw error;
      }

      const translatedExplanation = data.explanation;
      setExplanation(translatedExplanation);
      setIsAiGenerated(true);
      
      // Store the translation in session storage to avoid repeated API calls
      sessionStorage.setItem(
        `explanation-${question.id}-${selectedLanguage}`, 
        translatedExplanation
      );
      
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Failed to translate explanation. Please try again.');
      // Fallback to original explanation
      setExplanation(question.explanation || "No explanation available for this question.");
    } finally {
      setIsLoading(false);
    }
  };

  // Generate explanation if it doesn't exist
  const generateExplanation = async () => {
    if (explanation) return;
    
    setIsLoading(true);
    try {
      // Call the AI Tutoring service to generate an explanation
      const { data, error } = await supabase.functions.invoke('ai-tutoring', {
        body: {
          action: 'explain',
          data: {
            subject: question.category || question.subject || 'General Knowledge',
            question: question.question,
            options: question.options,
            correctAnswer: correctAnswer,
            language: 'english'
          }
        }
      });

      if (error) {
        throw error;
      }

      const generatedExplanation = data.explanation;
      setExplanation(generatedExplanation);
      setIsAiGenerated(true);
      
      // Store the explanation in session storage
      sessionStorage.setItem(
        `explanation-${question.id}-english`, 
        generatedExplanation
      );
      
    } catch (error) {
      console.error('Explanation generation error:', error);
      toast.error('Failed to generate explanation. Please try again.');
      setExplanation("No explanation available for this question.");
    } finally {
      setIsLoading(false);
    }
  };

  // Check if we have a cached explanation or if original explanation exists
  useEffect(() => {
    const cachedExplanation = sessionStorage.getItem(`explanation-${question.id}-${selectedLanguage}`);
    
    if (cachedExplanation) {
      setExplanation(cachedExplanation);
      setIsAiGenerated(true);
    } else if (selectedLanguage === "english") {
      if (question.explanation) {
        setExplanation(question.explanation);
        // Check if this is from PYQ Multiplier (metadata might indicate AI generation)
        const isFromPYQ = question.metadata && 
          (question.metadata.source === 'pyq_variation' || 
           question.metadata.variation_type === 'ai_generated');
        setIsAiGenerated(!!isFromPYQ);
      } else {
        setExplanation(null);
      }
    } else {
      setExplanation(null);
    }
  }, [question.id, selectedLanguage, question.explanation, question.metadata]);

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center">
            <Languages className="mr-2 h-5 w-5 text-primary" />
            Question {questionNumber} Explanation
          </div>
          {isAiGenerated && (
            <Badge variant="outline" className="flex items-center gap-1 text-xs bg-primary/5">
              <Sparkles className="h-3 w-3 text-primary" />
              AI Generated
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center mb-4">
            <span className="text-sm font-medium">Explanation Language:</span>
            <div className="flex gap-2 items-center flex-1">
              <Select
                value={selectedLanguage}
                onValueChange={setSelectedLanguage}
              >
                <SelectTrigger className="max-w-[200px]">
                  <SelectValue placeholder="Select Language" />
                </SelectTrigger>
                <SelectContent>
                  {supportedLanguages.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedLanguage !== "english" && !explanation && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={translateExplanation}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Translating...
                    </>
                  ) : (
                    <>
                      <MessageSquareText className="h-4 w-4 mr-1" />
                      Translate
                    </>
                  )}
                </Button>
              )}
              
              {!explanation && selectedLanguage === "english" && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={generateExplanation}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-1" />
                      Generate Explanation
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          
          <div className="rounded-md bg-muted/50 p-4">
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2">
                  {selectedLanguage === "english" 
                    ? "Generating explanation..." 
                    : `Translating explanation to ${supportedLanguages.find(l => l.value === selectedLanguage)?.label}...`}
                </span>
              </div>
            ) : explanation ? (
              <div className="prose max-w-none">
                <div dangerouslySetInnerHTML={{ __html: explanation.replace(/\n/g, '<br />') }} />
              </div>
            ) : (
              <p className="text-muted-foreground italic">
                {selectedLanguage === "english" 
                  ? "No explanation available. Click 'Generate Explanation' to create one."
                  : "Select a language and click 'Translate' to view the explanation in your preferred language."}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LanguageExplanation;
